import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { Injectable, OnDestroy } from '@angular/core';
import { BaseRestService } from './base-rest.service';
import { forkJoin, Observable, of, ReplaySubject } from 'rxjs';
import { catchError, concatMap, map, retry, take } from 'rxjs/operators';
import { ISubjectList, Subject } from '../models/subject';
import { HydrateSubjectService } from './hydrate-subject.service';
import { AutoUnsubscribe } from 'auto-unsubscribe.decorator';

@AutoUnsubscribe()
@Injectable({
  providedIn: 'root',
})
export class SearchService extends BaseRestService<Subject, ISubjectList> implements OnDestroy{

  onSearchStarted: ReplaySubject<any> = new ReplaySubject<any>(1, 100);
  onSearchResult: ReplaySubject<Subject[]> = new ReplaySubject<Subject[]>(1, 100);
  onMarkerSearchResult: ReplaySubject<Subject[]> = new ReplaySubject<Subject[]>(1, 100);
  searchTerm: string;
  searchResult: Subject[];
  includeArchived: boolean = false;

  constructor(
    http: HttpClient,
    private hydrateSubjectService: HydrateSubjectService,
    protected router: Router) {
    super(SearchService, http, 'api/search', 'search', router);

    // disable caching - not needed
    this.cacheEnabled = false;
  }

  ngOnDestroy(): void {}

  private search(notify: ReplaySubject<Subject[]>, type: string, searchTerm: string, subjectTypeIds?: string, markerIds?: string, includeArchived?: boolean) {
    this.onSearchStarted.next(undefined);
    this.searchTerm = searchTerm;
    this.getSearchData(type, searchTerm, subjectTypeIds, markerIds, includeArchived).pipe(take(1)).subscribe((data) => {
      this.searchResult = data;
      notify.next(this.searchResult);
    }, (error) => {
      this.searchResult = [];
      notify.next(this.searchResult);
    });
  }

  isNullOrWhitespace(input: string | null | undefined): boolean {
    return input === null || input === undefined || input.trim() === '';
  }

  private doGetSearchData(type: string, searchTerm: string, subjectTypeIds?: string, markerIds?: string, includeArchived?: boolean): Observable<Subject[]> {
    type ='subjectandreferral';
    //type = type || 'subjectandreferral';
    let urlParams = '';
    if (subjectTypeIds) urlParams = this.addUrlParam(urlParams, 'subjecttypes', subjectTypeIds);
    if (markerIds) urlParams = this.addUrlParam(urlParams, 'markers', markerIds);
    urlParams = this.addUrlParam(urlParams, 'includearchived', (includeArchived) ? 'true' : 'false');
    const getUrl = `${this.resourceUrl}/${type}/${encodeURIComponent(searchTerm.replace(/\//g, '|'))}` + ((urlParams) ? ('?' + urlParams) : '');
    this.log('Search url:' + getUrl);

    // Check to make sure we have a search term
    if (!searchTerm || this.isNullOrWhitespace(searchTerm)) {
      return of([]);
    }

    // build the observable and assign to a local variable so we can chain it later in this function
    // Note: we are using map to manipulate data and the http get will only be called when we subscribe to the chain of observables we will eventually return
    const http$ = this.http.get<Subject[]>(getUrl).pipe(
      retry({count: 3, delay: 3000}),
      map((data) => {

        this.debug('Data received from API:', getUrl, data);
        data = data || [];

        // now we have data returned we need to collect together the observables we will execute later...
        const hydrate$ = [];

        // ...starting with an observable which will return the data we are, eventually, going to return
        hydrate$.push(of(data));

        // the 'hydrate' function returns on observable for each item in the data array
        data.map((item) => { hydrate$.push(this.hydrate(item)); });

        // return the collection of observables ready for our chain
        return hydrate$;
      }),
      catchError(this.handleError<Subject>(`Get search`, true)),
    );

    return http$.pipe(concatMap((response: any[]) =>
      forkJoin(response).pipe(take(1), map((data) => {
        const result = (data[0] as any).map((i) => new Subject(i));
        // return our data
        return result;
      }),
        catchError(this.handleError<Subject>(`Get search - hydration`, false)),
      )));
  }

  getAll(type?: string, urlParams?: string) {
    // the doSearch has already retrieved our results - we therefore can retrieve the values and re-emit via the onChanged observable
    this.onChanged.next(this.searchResult);
  }

  // called by omni search on toolbar
  doSearch(searchTerm: string, subjectTypeIds?: string, includeArchived?: boolean) {
    this.includeArchived = includeArchived;
    this.search(this.onSearchResult, 'subject', searchTerm, subjectTypeIds, '', this.includeArchived);
  }

  doMarkerSearch(searchTerm: string, subjectTypeIds?: string, markerIds?: string) {
    this.includeArchived = false;
    this.search(this.onMarkerSearchResult, '', searchTerm, subjectTypeIds, markerIds);
  }

  getSearchData(type: string, searchTerm, subjectTypeIds?: string, markerIds?: string, includeArchived?: boolean): Observable<Subject[]> {
    return this.doGetSearchData(type, searchTerm, subjectTypeIds, markerIds, includeArchived);
  }

  hydrate(item: Subject): Observable<Subject> {
    return this.hydrateSubjectService.hydrate(item);
  }
}
